import { useCallback, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import cx from 'helpers/cx'

import { EditIcon } from '@pleio/icons/edit'
import { EditCrossIcon } from '@pleio/icons/edit-cross'
import { FaceIcon } from '@pleio/icons/face'
import { FaceCrossIcon } from '@pleio/icons/face-cross'
import { SettingsIcon } from '@pleio/icons/settings'

import IconButton from 'js/components/IconButton'
import { globalStateContext } from 'js/lib/GlobalState'

import styles from './AdminOverlay.module.css'

type Viewer = {
    isSubEditor?: boolean
    isSuspendAdminPrivileges?: boolean
    canAccessAdmin?: boolean
    showSuspendAdminPrivilegesToggle?: boolean
}

type Props = {
    viewer: Viewer
    editMode: boolean
    toggleEditMode: () => void
    positionLeft?: boolean
}

const AdminOverlay = ({
    viewer,
    editMode,
    toggleEditMode,
    positionLeft,
}: Props) => {
    const { globalState } = useContext(globalStateContext)

    const { t } = useTranslation()

    const [
        toggleAdminMode,
        { loading: toggleAdminModeLoading, error: toggleAdminModeError },
    ] = useMutation<any>(TOGGLE_ADMIN_MODE)

    const handleToggleAdminMode = async () => {
        await toggleAdminMode().then(() => {
            window.location.reload()
        })
    }

    if (toggleAdminModeError) {
        console.error(toggleAdminModeError)
    }

    // Detect Mac platform using userAgent (navigator.platform is deprecated)
    const platformIsMac =
        typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac')

    const shortKey = platformIsMac ? '⌘ + e' : 'Ctrl + e'

    const handleKeyDown = useCallback(
        (evt: KeyboardEvent) => {
            const modifierKey = platformIsMac ? evt.metaKey : evt.ctrlKey
            if (modifierKey && evt.key === 'e') {
                evt.preventDefault()
                toggleEditMode()
            }
        },
        [platformIsMac, toggleEditMode],
    )

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])

    if (
        !viewer?.isSubEditor &&
        !globalState?.canEditPage &&
        !viewer?.isSuspendAdminPrivileges
    ) {
        return null
    }

    return (
        <div
            className={cx(
                styles.wrapper,
                positionLeft && styles.positionLeft,
                toggleAdminModeLoading && styles.active,
            )}
        >
            {!viewer.isSuspendAdminPrivileges && (
                <IconButton
                    className={styles.adminEditModeButton}
                    variant={editMode ? 'secondary' : 'pleio'}
                    size="huge"
                    dropShadow
                    onClick={toggleEditMode}
                    tooltip={
                        editMode
                            ? t('admin.edit-mode-off', {
                                  shortKey,
                              })
                            : t('admin.edit-mode-on', {
                                  shortKey,
                              })
                    }
                    tooltipProps={{ side: 'left' }}
                >
                    {editMode ? <EditCrossIcon /> : <EditIcon />}
                </IconButton>
            )}

            {viewer.canAccessAdmin && (
                <IconButton
                    className={styles.adminSettingsButton}
                    as={Link as any}
                    to="/admin"
                    variant="pleio"
                    {...({} as any)}
                    size="huge"
                    tooltip={t('admin.goto-admin')}
                    dropShadow
                    tooltipProps={{ side: 'left' }}
                >
                    <SettingsIcon />
                </IconButton>
            )}

            {viewer.showSuspendAdminPrivilegesToggle && (
                <IconButton
                    className={cx(
                        styles.toggleUserButton,
                        viewer.isSuspendAdminPrivileges && styles.active,
                    )}
                    onClick={handleToggleAdminMode}
                    variant={
                        viewer.isSuspendAdminPrivileges ? 'secondary' : 'pleio'
                    }
                    size="huge"
                    tooltip={
                        viewer.isSuspendAdminPrivileges
                            ? t('admin.disable-user-mode')
                            : t('admin.user-mode')
                    }
                    dropShadow
                    loading={toggleAdminModeLoading}
                    tooltipProps={{ side: 'left' }}
                >
                    {viewer.isSuspendAdminPrivileges ? (
                        <FaceCrossIcon />
                    ) : (
                        <FaceIcon />
                    )}
                </IconButton>
            )}
        </div>
    )
}

const TOGGLE_ADMIN_MODE = gql`
    mutation ToggleSuspendAdminPrivileges {
        toggleSuspendAdminPrivileges {
            guid
            showSuspendAdminPrivilegesToggle
            isSuspendAdminPrivileges
        }
    }
`

export default AdminOverlay
